"use client";

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useLanguage } from '@/context/LanguageContext';
import { ChevronRightIcon, HomeIcon } from 'lucide-react';

interface BreadcrumbsProps {
  homeLabel?: string;
  className?: string;
  omitCurrent?: boolean;
}

/**
 * A component that renders breadcrumb navigation based on the current URL path
 */
const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  homeLabel,
  className = '',
  omitCurrent = false
}) => {
  const pathname = usePathname();
  const { translate, language } = useLanguage();

  // Map path segments to their translated labels
  const pathToLabel: Record<string, string> = {
    'chalet': translate('nav.property', 'Chalet'),
    'amenities': translate('nav.property.amenities', 'Amenities'),
    'features': translate('nav.property.features', 'Features'),
    'gallery': translate('nav.property.gallery', 'Photo Gallery'),
    'attractions': translate('nav.location.attractions', 'Local Attractions'),
    'transport': translate('nav.location.transport', 'Transport'),
    'map': translate('nav.location.map', 'Interactive Map'),
    'navigation': translate('nav.location.gps', 'Find Your Way (GPS)'),
    'activities': translate('nav.activities', 'Activities'),
    'skiing': translate('nav.activities.skiing', 'Skiing & Snowboarding'),
    'summer': translate('nav.activities.summer', 'Summer Activities'),
    'family-accommodation': translate('nav.activities.family', 'Family Adventures'),
    'seasonal': translate('nav.activities.seasonal', 'Seasonal Events'),
    'booking': translate('nav.booking', 'Booking'),
    'availability': translate('nav.booking.availability', 'Check Availability'),
    'prices': translate('nav.booking.prices', 'Prices & Offers'),
    'process': translate('nav.booking.process', 'Booking Process'),
    'faq': translate('nav.booking.faq', 'FAQs'),
    'reviews': translate('nav.reviews', 'Reviews'),
    'contact': translate('nav.contact', 'Contact'),
    'directions': translate('nav.contact.directions', 'Directions'),
    'inquiry': translate('nav.contact.inquiry', 'Inquiry Form'),
    'engelberg-accommodation': '', // Skip this in breadcrumbs as it's just for SEO
    'sustainable-accommodation': translate('nav.property.sustainable', 'Sustainable Accommodation'),
    'ski-chalet': translate('nav.activities.skiing', 'Skiing & Snowboarding'),
  };

  // Skip language segment from breadcrumbs
  const langRegex = /^\/(en|de|fr|es|ro|engelberg-accommodation)\//;
  const pathWithoutLang = pathname.replace(langRegex, '/');

  // Generate breadcrumb items
  const generateBreadcrumbs = () => {
    // Start with home
    const home = {
      label: homeLabel || translate('nav.home', 'Home'),
      path: '/'
    };

    const segments = pathWithoutLang.split('/').filter(Boolean);
    let currentPath = '';
    const breadcrumbs = [home];

    segments.forEach((segment, index) => {
      currentPath += `/${segment}`;

      // Skip the current page if omitCurrent is true and this is the last segment
      if (omitCurrent && index === segments.length - 1) {
        return;
      }

      // Get label for the path segment
      let label = pathToLabel[segment] || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');

      breadcrumbs.push({
        label,
        path: currentPath
      });
    });

    return breadcrumbs;
  };

  const breadcrumbItems = generateBreadcrumbs();

  // Don't render breadcrumbs on the home page
  if (pathname === '/' || breadcrumbItems.length <= 1) {
    return null;
  }

  // Prepare the URL with current language for the links
  const getUrlWithLang = (path: string) => {
    if (path === '/') {
      return `/${language}`;
    }
    return `/${language}${path}`;
  };

  return (
    <nav aria-label="Breadcrumbs" className={`py-2 ${className}`}>
      <div className="bg-gray-50 px-3 py-1.5 rounded border-b border-gray-200">
        <ol className="flex items-center flex-wrap text-sm">
          {breadcrumbItems.map((item, index) => {
            const isLast = index === breadcrumbItems.length - 1;

            return (
              <li key={item.path} className="flex items-center">
                {index === 0 ? (
                  // Home item with icon
                  <Link
                    href={getUrlWithLang(item.path)}
                    className="flex items-center text-gray-600 hover:text-alpine-blue-600 transition-colors duration-200 hover:scale-105 transform"
                    aria-label={translate('breadcrumb.home', 'Home')}
                  >
                    <div className="p-1 rounded-full">
                      <HomeIcon className="h-3 w-3 text-alpine-blue-600" />
                    </div>
                    <span className="sr-only">{item.label}</span>
                  </Link>
                ) : (
                  // Other items as text
                  <Link
                    href={getUrlWithLang(item.path)}
                    className={`transition-all duration-200 ${isLast
                      ? 'text-alpine-blue-700 font-semibold pointer-events-none'
                      : 'text-gray-600 hover:text-alpine-blue-600 hover:underline hover:scale-105 transform'}`}
                    aria-current={isLast ? 'page' : undefined}
                  >
                    {item.label}
                  </Link>
                )}

                {/* Add separator after all items except the last one */}
                {!isLast && (
                  <ChevronRightIcon className="h-3 w-3 mx-1.5 text-gray-400" aria-hidden="true" />
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
};

export default Breadcrumbs;